import { formatAddressLine, pickPrimaryAddress, type AddressData } from "./address";
import { parseManipulatedPayload } from "./manipulatedPayload";
import type { PrescriptionDocumentData } from "./renderPrescriptionBody";

type Weight = { weight: number | null; measured_at: string | null; medical_record_id: string | null; deleted_at: string | null };

export interface PrescriptionSourceRecord {
  type: string | null; issued_at: string | null; validity_in_days: number | null; observations: string | null;
  control_regime: string | null; mapa_number: string | null; mapa_registration_snapshot: string | null;
  mapa_prescription_identifier: string | null; manipulated_payload: unknown; medical_record_id: string | null;
  issuer_address: string | null;
  buyer_mode?: string | null; buyer_snapshot?: unknown;
  items: Array<{ medication_name: string | null; medication_concentration: string | null; presentation: string | null;
    quantity: string | null; pharmaceutical_form: string | null; administration_route: string | null;
    posology_mode: string | null; free_text_posology: string | null; dose_amount: string | null; dose_unit: string | null;
    frequency_value: string | null; frequency_unit: string | null; duration_value: string | null; duration_unit: string | null }>;
  exam_items: Array<{ exam_name: string | null; instructions: string | null }>;
  pet: { name: string | null; birth_date: string | null; species: string | null; breed: string | null; gender: string | null;
    weights: Weight[]; owner: { name: string | null; document: string | null; addresses: AddressData[] } | null } | null;
  vet: { name: string; crmvForPrint: string; phone: string | null; address: string | null };
  clinic: { name: string | null };
}

const norm = (v: string | null | undefined) => (typeof v === "string" ? v.trim() : "");
const time = (v: string | null) => (v ? new Date(v).getTime() : 0);
const byMeasuredDesc = (a: Weight, b: Weight) => time(b.measured_at) - time(a.measured_at);

/** Same-record weight first, else most recent; deleted ignored. Mirrors the PDF Lambda's historical rule. */
export function pickPetWeight(weights: Weight[] | null | undefined, medicalRecordId: string | null): number | null {
  if (!weights?.length) return null;
  const active = weights.filter((w) => !w.deleted_at);
  if (active.length === 0) return null;
  if (medicalRecordId) {
    const linked = active.filter((w) => w.medical_record_id === medicalRecordId);
    if (linked.length > 0) return [...linked].sort(byMeasuredDesc)[0]?.weight ?? null;
  }
  return [...active].sort(byMeasuredDesc)[0]?.weight ?? null;
}

function buildBuyer(mode: string | null | undefined, snapshot: unknown) {
  if (mode !== "filled" || !snapshot || typeof snapshot !== "object") return null;
  const s = snapshot as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  return { name: pick(s.name), document: pick(s.document), address: pick(s.address), cityUf: pick(s.city_uf), phone: pick(s.phone) };
}

export function buildPrescriptionDocumentData(r: PrescriptionSourceRecord): PrescriptionDocumentData {
  const pet = r.pet;
  return {
    type: r.type ?? "standard",
    issuedAt: r.issued_at,
    validityInDays: r.validity_in_days,
    observations: r.observations,
    controlRegime: r.control_regime,
    mapaNumber: r.mapa_number,
    mapaRegistration: r.mapa_registration_snapshot,
    mapaPrescriptionIdentifier: r.mapa_prescription_identifier,
    vetName: r.vet.name,
    crmv: r.vet.crmvForPrint,
    vetPhone: r.vet.phone,
    vetAddress: norm(r.issuer_address) || norm(r.vet.address) || null,
    clinicName: r.clinic.name ?? "",
    manipulatedPayload: r.type === "manipulated" ? parseManipulatedPayload(r.manipulated_payload) : null,
    buyer: buildBuyer(r.buyer_mode, r.buyer_snapshot),
    pet: {
      name: pet?.name ?? "",
      ownerName: pet?.owner?.name ?? "",
      ownerDocument: pet?.owner?.document ?? null,
      ownerAddress: formatAddressLine(pickPrimaryAddress(pet?.owner?.addresses ?? null)) || null,
      species: pet?.species ?? null,
      breed: pet?.breed ?? null,
      gender: pet?.gender ?? null,
      birthDate: pet?.birth_date ?? null,
      weightKg: pickPetWeight(pet?.weights, r.medical_record_id),
    },
    items: r.items.map((i) => ({
      medicationName: norm(i.medication_name), concentration: i.medication_concentration, presentation: i.presentation,
      quantity: i.quantity, pharmaceuticalForm: i.pharmaceutical_form, administrationRoute: i.administration_route,
      posology_mode: i.posology_mode, free_text_posology: i.free_text_posology, dose_amount: i.dose_amount, dose_unit: i.dose_unit,
      frequency_value: i.frequency_value, frequency_unit: i.frequency_unit, duration_value: i.duration_value, duration_unit: i.duration_unit,
    })),
    examItems: r.exam_items.map((e) => ({ examName: norm(e.exam_name), instructions: e.instructions })),
  };
}
